import { Input } from '@/components/shared/Input/Input'
import { Button } from '@/components/ui/button'
import {
  useSupportEmailVerification,
  UseSupportEmailVerificationOptions,
} from '@/hooks/useSupportEmailVerification'
import { useTranslation } from 'react-i18next'
import { OtpCodeInput } from './otp-code-input'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

export interface SupportEmailVerificationProps
  extends UseSupportEmailVerificationOptions {
  className?: string
  otpDataCyPrefix?: string
  leadingActions?: React.ReactNode
  hideActions?: boolean
  showSentMessage?: boolean
}

export function EmailVerification({
  className,
  otpDataCyPrefix = 'support-email-otp',
  leadingActions,
  hideActions = false,
  showSentMessage = true,
  ...verificationOptions
}: SupportEmailVerificationProps) {
  const { t } = useTranslation(['common', 'onboarding'])

  const {
    emailSupport,
    verify,
    codeError,
    otpDigits,
    otpDisabled,
    otpInputRefs,
    resendCooldownSec,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleResendCode,
  } = useSupportEmailVerification(verificationOptions)

  const otpAriaLabel = t('supportEmailVerificationEnterCode')
  const isSending = verify.busy === 'send' && verify.phase === 'idle'
  const codeWasSent = verify.phase === 'awaiting_code'

  return (
    <div className={cn('space-y-6', className)}>
      <div>
        <Input label={t('supportEmailLabel')} value={emailSupport} disabled />
        {isSending && (
          <p className="text-sm mt-2 text-muted flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('supportEmailVerificationSendingEmail')}
          </p>
        )}
        {showSentMessage && codeWasSent && (
          <p className="text-sm mt-2 text-[#A9D342]">
            {t('supportEmailVerificationCodeSent')}
          </p>
        )}
      </div>

      <OtpCodeInput
        digits={otpDigits.split('')}
        disabled={otpDisabled}
        inputRefs={otpInputRefs}
        onChange={handleOtpChange}
        onKeyDown={handleOtpKeyDown}
        onPaste={handleOtpPaste}
        ariaLabel={otpAriaLabel}
        dataCyPrefix={otpDataCyPrefix}
      />

      {codeError && (
        <p
          className="text-sm text-error text-center"
          data-cy="support-email-verification-error"
        >
          {codeError}
        </p>
      )}

      {!hideActions && (
        <div className="flex justify-center gap-6 mt-8 flex-wrap">
          {leadingActions}
          <Button
            type="button"
            onClick={handleResendCode}
            variant="ghost"
            disabled={
              verify.busy === 'send' ||
              verify.busy === 'verify' ||
              verify.busy === 'resend' ||
              resendCooldownSec > 0
            }
          >
            {verify.busy === 'resend'
              ? t('supportEmailVerificationResending')
              : resendCooldownSec > 0
              ? t('supportEmailVerificationResendWait', {
                  seconds: resendCooldownSec,
                })
              : t('supportEmailVerificationResendCode')}
          </Button>
        </div>
      )}
    </div>
  )
}
